import { Feature, IDynamicLoader, Inject } from '@tencent/workbench';
import { IChartAxisSettingsFeature, IChartAxisSettingsShower as ChartAxisSettingsShower } from '@tencent/chart-drawer-components/lib/interface';
import type { IChartAxisSettingsShower } from '@tencent/chart-drawer-components/lib/interface';
import { SLIDE_ADAPTER } from '@src/framework/workbench/editor-adpater/slide-adapter';
import isFinite from 'lodash/isFinite';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import i18n from '@i18next/i18n';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import ChartBaseDrawerFeature from '@src/framework/workbench/drawer/ui/chart/chart-base-drawer-feature';
import { IAxisProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis.interface';
import { INPUT_PROPS_CALLBACK_KEY } from '../../../constants';
import { NumberInputFrom } from '@tencent/wb-ext-form';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';


const DEFAULT_LOG_BASE = 10;

export const LogBaseOptions = {
    step: 1,
    min: 2,
    max: 1000,
};
const MIN_MAX_VALUE = {
    min: 1,
    max: 100,
};

@Feature({
    name: 'ChartAxisFeature',
    adapters: [SLIDE_ADAPTER],
    provide: IChartAxisSettingsFeature,
})
export class ValueAxisFeature extends ChartBaseDrawerFeature implements IChartAxisSettingsFeature {
    private logBase = DEFAULT_LOG_BASE;

    /**
     * 缓存修改图表的选区
     */
    private axis: IAxisProperty | undefined;
    private hasInited: boolean;

    private get shower() {
        return (this.loader.instance ?? this.loader) as IChartAxisSettingsShower;
    }

    public constructor(@Inject(ChartAxisSettingsShower) private loader: IDynamicLoader<IChartAxisSettingsShower>) {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS);
    }

    public initPropsCallback =  async () => {
        this.shower.initPropsCallback?.();
    };

    /**
     * 设置最小值
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS, 'initAxis', 'resetAxis')
    public setMin(value: number | null): void {
        value = typeof value === 'number' ? this.getValidValue({ min: value }, NumberInputFrom.INPUT, MIN_MAX_VALUE.max, MIN_MAX_VALUE.min).min : value;
        if (this.isInputOnlyChangeUI()) return;
        if (value === this.shower.getMin()) {
            return;
        }
        const max = this.shower.getMax();
        const isValid = this.maxLessThenMin(max, value);
        if (!isValid) {
            Snackbar.show({
                type: 'info',
                message: i18n.t('坐标轴最大值应大于最小值，否则无效。'),
            });
            return;
        }

        if (this.axis) {
            let realValue = value;

            /**
             * 当前图表如果使用了对数刻度，且用户主动设置最小值为小于等于 1 的数
             * 需要强制将最小值复原为默认值，即 1
             */
            const isLogScale = this.shower.getLogValue();
            if (isLogScale && !value) {
                realValue = 1;
            }
            if (value === null) {
                this.axis.updateProps(undefined, {
                    scaling: {
                        min: value,
                    },
                });
                return;
            }
            this.axis.updateProps({
                scaling: {
                    min: realValue!,
                },
            });
        }
    }

    /**
     * 设置最大值
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS, 'initAxis', 'resetAxis')
    public setMax(value: number | null): void {
        value = typeof value === 'number' ? this.getValidValue({ max: value }, NumberInputFrom.INPUT, MIN_MAX_VALUE.max, MIN_MAX_VALUE.min).max : value;
        if (this.isInputOnlyChangeUI()) return;
        if (value === this.shower.getMax()) {
            return;
        }

        const min = this.shower.getMin();
        const isValid =  this.maxLessThenMin(value, min);
        if (!isValid) {
            Snackbar.show({
                type: 'info',
                message: i18n.t('坐标轴最大值应大于最小值，否则无效。'),
            });
            return;
        }

        if (this.axis) {
            if (value === null) {
                this.axis.updateProps(undefined, {
                    scaling: {
                        max: value,
                    },
                });
                return;
            }
            this.axis.updateProps({
                scaling: {
                    max: value,
                },
            });
        }
    }

    /**
     * 设置格式
     */
    public setFormat =  async (format: string): Promise<void> => {
        if (format === this.shower.getFormat()) {
            return;
        }
        const model = this.getSelectModel() as IChartModel;
        if (model?.type !== UIModelType.CHART) return;
        const axis = model.getSelectedElementProperty() as IAxisProperty;
        if (!axis) return;
        axis?.updateProps({
            numFmt: {
                formatCode: format,
            },
        });
    };

    /**
     * 设置 等份(主) 值
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS, 'initAxis', 'resetAxis')
    public setMajorUnitValue(value: number | null) {
        value = typeof value === 'number' ? this.getValidValue({ majorUnit: value }, NumberInputFrom.INPUT).majorUnit : value;
        if (this.isInputOnlyChangeUI()) return;
        if (value === this.shower.getMajorUnitValue()) {
            return;
        }

        const minorUnit = this.shower.getMinorUnitValue();
        if (!this.axis) return;
        if (value === null) {
            this.axis?.updateProps(undefined, {
                majorUnit: value,
            });
        }
        if (Number(value) <= 0) return;
        if (minorUnit && value && minorUnit > value) {
            return this.axis?.updateProps({
                minorUnit: value,
                majorUnit: value,
            });
        }
        this.axis?.updateProps({
            majorUnit: value!,
        });
    }

    /**
     * 设置 等份(次) 值
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS, 'initAxis', 'resetAxis')
    public setMinorUnitValue(value: number | null) {
        value = typeof value === 'number' ? this.getValidValue({ minorUnit: value }, NumberInputFrom.INPUT).minorUnit : value;
        if (this.isInputOnlyChangeUI()) return;
        if (value === this.shower.getMinorUnitValue()) {
            return;
        }

        const majorUnit = this.shower.getMajorUnitValue();
        if (!this.axis) return;
        if (value === null) {
            this.axis?.updateProps(undefined, {
                minorUnit: value,
            });
        }
        if (Number(value) <= 0) return;
        if (majorUnit && value && majorUnit < value) {
            return this.axis?.updateProps({
                minorUnit: value,
                majorUnit: value,
            });
        }
        this.axis?.updateProps({
            minorUnit: value!,
        });
    }

    /**
     * 设置 对数 - 对数刻度 值
     */
    public setLogValue(value: boolean) {
        const model = this.getSelectModel() as IChartModel;
        if (model?.type !== UIModelType.CHART) return;
        const axis = model.getSelectedElementProperty() as IAxisProperty;
        if (!axis) return;
        if (!value) {
            axis?.updateProps(undefined, {
                scaling: {
                    logBase: null,
                },
            });
            return;
        }
        if (!this.logBase) {
            this.logBase = DEFAULT_LOG_BASE;
        }
        axis?.updateProps({
            scaling: {
                logBase: this.logBase,
            },
        });
    }

    /**
     * 设置 对数 - 底数 值
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.CHART_VALUE_AXIS, 'initAxis', 'resetAxis')
    public setLogBaseValue(value: number) {
        const { logBaseValue } = this.getValidValue(
            { logBaseValue: value }, NumberInputFrom.INPUT,
            LogBaseOptions.max, LogBaseOptions.min,
        );
        this.logBase = logBaseValue;
        if (this.isInputOnlyChangeUI()) return;
        if (this.shower.getLogBaseInfo?.() === logBaseValue) return;
        this.handleLogBaseChange(logBaseValue);
    }

    public handleLogBaseChange = (value: number) => {
        if (!this.axis) return;
        this.axis?.updateProps({
            scaling: {
                logBase: value,
            },
        });
    };

    /**
     * 判断坐标轴最大最小值是否合法，最大值应该大于最小值，不合法会弹出 tips 提示
     * @param max 最大值
     * @param min 最小值
     */
    private maxLessThenMin(max?: number | null, min?: number | null) {
        if (!isFinite(max) || !isFinite(min)) {
            // 只要有一个值时 undefined 或者 null，代表自动识别，属于合法情况，不用提示，直接返回
            return true;
        }

        if (max! >= min!) {
            // 最大值不小于最小值，属于合法情况，不用提示，直接返回
            return true;
        }

        return false;
    }

    // 传递给 handleInputBlurInitAndReset 装饰符调用
    private initAxis = (): void => {
        if (this.hasInited) return;
        const model = this.getSelectModel();
        if (model?.type !== UIModelType.CHART) return;
        const axis = model.getSelectedElementProperty() as IAxisProperty;
        if (typeof this.axis === 'undefined' && axis) {
            this.axis = axis;
        }
        this.hasInited = true;
    };

    // 传递给 handleInputBlurInitAndReset 装饰符调用
    private resetAxis = (): void => {
        this.axis = undefined;
        this.hasInited = false;
    };
}
